<template>
  <div ref="buynow">
    <transition name="fade">
      <div
        v-if="showStickyBuyNow && !isPricingCardEmailSubmitted"
        class="bottom-container"
      >
        <DealBannerHeader
          v-if="showStickyBanner"
          :banner-details="deal.banner_details"
          :display-timer="displayTimer"
          mobile-sticky-timer
          class="d-flex d-lg-none pl-2 sticky-banner"
        />
        <div
          class="pdp-buynow-mobile-head d-lg-none d-flex align-items-center
                justify-content-around bg-white"
          :class="{ 'sticky-box-shadow': !showStickyBanner }"
        >
          <div
            v-if="deal.default_price"
            class="px-2"
            :class="{ 'text-center': !inactiveIndefinitely }"
          >
            <div
              v-if="deal.is_info || inactiveIndefinitely"
            >
              <span
                v-if="deal.is_info"
                class="deal-unavailable-sticky"
              >
                <b>{{ infoDealQuestionText }} </b>
                Let us know!
              </span>
              <span
                v-else-if="inactiveIndefinitely"
                class="deal-unavailable-sticky"
              >
                <b>This deal is unavailable </b>
                — Sign up to get our latest deals!
              </span>
            </div>
            <div
              v-else
              class="pdp-buynow-price-sticky p-0 m-0"
            >
              <span v-if="selectedPlan">{{ selectedPriceText }}</span>
              <span v-else>{{ defaultPriceText }}</span>
              <span
                v-if="deal.default_original_price"
                class="pdp-buynow-old-price"
              >
                {{ deal.default_original_price | currencyWithOptionalDecimal }}
              </span>
            </div>
          </div>
          <!-- an: make button full width in case no default_price usign flex-grow -->
          <div
            class="px-2"
            :class="deal.default_price ? 'flex-shrink-0' : 'flex-grow-1'"
          >
            <DButton
              v-if="deal.is_info || inactiveIndefinitely"
              class="pdp-buynow-plus-mobile"
              @click="onClickEmailMeMobile"
            >
              {{ stickyFooterButtonText }}
            </DButton>
            <!-- if deal has clickthrough, use a link button to send them to that URL -->
            <a
              v-else-if="clickthroughUrl"
              class="btn btn-primary pdp-buynow-btn-mobile"
              :href="clickthroughUrl"
            >
              {{ buyButtonText }}
            </a>
            <!-- jw: if the user doesn't have plus link them to learn more about it. -->
            <a
              v-else-if="notPlusAndEarlyAccessOrLastCall"
              :href="`/plus/?ref=plus-page-${deal.slug}`"
              class="btn btn-primary pdp-buynow-plus-mobile"
            >
              {{ buyButtonText }}
            </a>
            <!-- jw: if the user does have plus then allow them to buy it like normal. -->
            <button
              v-else
              class="btn btn-primary pdp-buynow-btn-mobile"
              :disabled="disabledBuyButton"
              @click="postSignupOrAddToCart()"
              @keydown.enter="postSignupOrAddToCart()"
            >
              {{ buyButtonText }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import dayjs from 'dayjs';

// TODO:BRO-1276: replace DTacoRating, DDropdownItem, DButton where possible
import { DButton } from '@appsumo/dorado-button';
import SubmitEmailMixin from '../../mixins/submit-email';
import PDPScrollToElementMixin from './mixins/PDPScrollToElementMixin';
import { LISTING_TYPES_WITH_LOGO } from '~/constants/listing-types';
import { DEAL_STAGE } from '~/constants/deal-stages';
import {
  CART_LOGIN_REDIRECT_EXPERIMENT_NAME,
  COMING_SOON_BANNER_TEXT,
  WAITLIST_URL,
  MOBILE_STICKY_COUNTDOWN_TIMER_EXPERIMENT_NAME,
} from '../../constants/global';

export default {
  LISTING_TYPES_WITH_LOGO,
  components: {
    DButton,
    DealBannerHeader: () => import('./DealBannerHeader.vue'),
  },
  mixins: [SubmitEmailMixin, PDPScrollToElementMixin],
  props: {
    deal: {
      type: Object,
      required: true,
    },
    buyButton: {
      type: Object,
      required: true,
    },
    plans: {
      type: Array,
      required: false,
      default: () => [],
    },
    // TODO temp user object to be replaced by this.$store.state.session.user
    user: {
      type: Object,
      required: true,
    },
    isPricingCardEmailSubmitted: {
      type: Boolean,
      required: true,
    },
    bannerDetails: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isDisabled: false,
      showStickyBuyNow: false,
      mobileMatchMedia: null,
      buyNowObserver: null,
      footerObserver: null,
    };
  },
  computed: {
    selectedPlan() {
      return this.$store.state.selectedPlan?.plan;
    },
    infoDealQuestionText() {
      return `Want to see ${this.deal.public_name} on AppSumo?`;
    },
    stickyFooterButtonText() {
      return this.deal.is_info ? 'I\'d buy it' : 'Email me';
    },
    buyButtonText() {
      if (this.buyButton.is_draft) {
        return 'Buy now (Draft)';
      }

      // is_coming_soon will be true if the current user does not have early access, so we want
      //     to handle these cases first since early access behavior should trump coming soon.
      //
      // note: buyButton.is_coming_soon behaves slightly differently than our bannerDetails
      // coming soon, so we're including the banner details check here to match buy button text
      // to our pdp stage banner as a temporary fix
      if (!this.earlyAccessOrLastCall
        && (this.bannerDetails?.text === COMING_SOON_BANNER_TEXT || this.buyButton.is_coming_soon)
      ) {
        return 'Coming soon';
      }

      if (this.buyButton.is_ended) {
        return 'Sold out';
      }

      if (this.buyButton.is_get_now) {
        return this.deal.is_free ? 'Get now for FREE' : 'Get now';
      }

      return this.deal.is_free ? 'Get now for FREE' : 'Buy now';
    },
    clickthroughUrl() {
      return this.deal.clickthrough_url;
    },
    disabledBuyButton() {
      return this.disabledState || this.buyButton.is_coming_soon;
    },
    defaultPriceText() {
      const formattedPrice = this
        .$options
        .filters
        .currencyWithOptionalDecimal(this.deal.default_price);

      if (this.deal.plan_stack_count > 1) {
        return `Starting at ${formattedPrice}`;
      }

      return formattedPrice;
    },
    selectedPriceText() {
      if (this.selectedPlan) {
        return this.getPlanDisplayText(this.selectedPlan);
      }

      return this.defaultPriceText;
    },
    hasPlans() {
      return this.plans && this.plans.length > 1;
    },
    disabledState() {
      if (this.buyButton.is_ended) {
        return true;
      }
      if (this.hasPlans) {
        return !this.selectedPlan;
      }
      return this.isDisabled;
    },
    stageDetails() {
      return this.deal.stage_details || {};
    },
    currentlyBeforeNextDealStage() {
      const nextStageStartDate = this.stageDetails.next_stage_start_date;
      if (!nextStageStartDate) {
        return false;
      }

      const dayjsNow = dayjs();
      const diffInMilliseconds = dayjs(nextStageStartDate).diff(dayjsNow);

      return diffInMilliseconds > 0;
    },
    lowCodesRemaining() {
      return this.deal.shows_codes_remaining && this.deal.codes_remaining > 0;
    },
    displayTimer() {
      return this.deal.active_timer
          && this.stageDetails.current_stage !== DEAL_STAGE.INACTIVE
          && this.currentlyBeforeNextDealStage;
    },
    inactiveIndefinitely() {
      return this.stageDetails.is_inactive_indefinitely;
    },
    earlyAccessOrLastCall() {
      return this.buyButton.is_early_access || this.buyButton.is_last_call;
    },
    notPlusAndEarlyAccessOrLastCall() {
      return !this.user.hasPlus && this.earlyAccessOrLastCall;
    },
    redirectAnonUserToLogin() {
      return this.$store.getters['experiments/getExperimentVariant'](CART_LOGIN_REDIRECT_EXPERIMENT_NAME) === 'enabled';
    },
    currentUser() {
      return this.$store.state.user;
    },
    showStickyBanner() {
      const experimentActive = this.$store.state.experiments[
        MOBILE_STICKY_COUNTDOWN_TIMER_EXPERIMENT_NAME
      ] === 'enabled';
      const endingSoon = this.deal?.banner_details?.text === 'Ending soon';

      return experimentActive && endingSoon && this.currentlyBeforeNextDealStage;
    },
  },
  watch: {
    showStickyBanner(val) {
      this.$store.dispatch(
        'scrollToTop/updateHeight',
        { showHigher: val },
      );
    },
  },
  mounted() {
    this.mobileMatchMedia = window.matchMedia('(max-width: 991px)');
    this.mobileMatchMedia.addListener(this.toggleObservers);

    this.toggleObservers(this.mobileMatchMedia);

    this.$store.dispatch(
      'scrollToTop/updateHeight',
      { showHigher: this.showStickyBanner },
    );
  },
  beforeDestroy() {
    this.mobileMatchMedia.removeListener(this.toggleObservers);
    this.destroyObservers();
  },
  methods: {
    // seems silly, but let's centralize this common format into a helper function.
    getPlanDisplayText(plan) {
      const name = this.deal.use_licensing ? plan.public_name : this.pluralizeCodeText(plan.codes);

      return `${name} : ${this.$options.filters.currencyWithOptionalDecimal(plan.price)}`;
    },
    pluralizeCodeText(code) {
      return `${code} Code${code > 1 ? 's' : ''}`;
    },
    onClickEmailMeMobile() {
      const emailCaptureElement = document.querySelector('.sold-out-email-capture');

      if (emailCaptureElement) {
        this.$_scrollToElement(emailCaptureElement);
      }
    },
    toggleObservers(event) {
      if (event.matches) {
        this.registerBuyNowObserver();
      } else {
        // remove any observer
        this.showStickyBuyNow = false;
        this.destroyObservers();
      }
    },
    registerBuyNowObserver() {
      this.buyNowObserver = new IntersectionObserver((entries) => {
        const [buyNowEntry] = entries;
        const isAfterViewport = buyNowEntry.boundingClientRect.bottom <= 0;

        if (!buyNowEntry.isIntersecting && isAfterViewport) {
          this.showStickyBuyNow = true;
          this.registerFooterObserver();
        } else if (this.footerObserver) {
          this.showStickyBuyNow = false;
          this.footerObserver.disconnect();
          this.footerObserver = null;
        }
      });

      this.buyNowObserver.observe(this.$refs.buynow);
    },
    registerFooterObserver() {
      const footer = document.querySelector('footer');

      this.footerObserver = new IntersectionObserver((entries) => {
        const [footerEntry] = entries;
        this.showStickyBuyNow = !footerEntry.isIntersecting;
      });

      this.footerObserver.observe(footer);
    },
    destroyObservers() {
      if (this.buyNowObserver) {
        this.buyNowObserver.disconnect();
        this.buyNowObserver = null;
      }

      if (this.footerObserver) {
        this.footerObserver.disconnect();
        this.footerObserver = null;
      }
    },
    async postSignupOrAddToCart() {
      if (this.buyButton.is_ended) {
        await this.postEmailWaitlistSignUp();
      } else {
        this.postAddToCart();
      }
    },
    postAddToCart() {
      this.isDisabled = true;
      const data = new FormData();
      data.append('id', this.deal.id);
      if (this.selectedPlan) {
        data.append('plan_id', this.selectedPlan.id);
        data.append('quantity', this.selectedPlan.codes);
      } else if (this.deal.default_plan) {
        data.append('plan_id', this.deal.default_plan.id);
      }
      if (this.deal.is_free) {
        const quantity = (this.deal.use_licensing) ? 1 : this.deal.default_plan.codes;
        data.append('quantity', quantity);
      }
      axios.post('/cart/add/', data, this.$_formHeaders())
        .then(() => {
          this.isDisabled = false;
          if (window) {
            // if user is not logged in, and experiment is enabled
            if (!this.currentUser?.isAuthenticated && this.redirectAnonUserToLogin) {
              window.location = '/accounts/login/?next=/cart/';
            } else {
              window.location = '/cart/';
            }
          }
        })
        .catch(() => {
          this.$toastr.e('Something has gone wrong with this request. Please try again.');
          this.isDisabled = false;
        });
    },
    async postEmailWaitlistSignUp() {
      if (!this.$_validateEmail(this.emailAddress)) {
        this.$toastr.w('Please use a valid email address.');
      } else {
        const data = this.$_createFormDataWithUtm();
        data.append('deal_slug', this.deal.slug);
        data.append('waitlist_email', this.emailAddress);

        const showToastMessageOnSuccess = false;
        await this.$_sendEmailRequest(WAITLIST_URL, data, showToastMessageOnSuccess);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~/styles/dorado/themes/dorado';
  @import '../../styles/abstracts/mixins';
  @import '../../styles/utils/breakpoints';

  .bottom-container {
    bottom: 0;
    position: fixed;
    left: 0;
    right: 0;
    z-index: 9000;
  }

  .sticky-banner,
  .pdp-buynow-mobile-head {
    padding: 10px 5px;
  }

  .sticky-box-shadow {
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
  }

  /* stylelint-disable selector-no-qualifying-type */
  span.pdp-buynow-old-price {
    text-decoration: line-through;
    color: #8e9baa;
    font-size: 16px;
    line-height: 21px;
  }

  span.pdp-buynow-mobile-head,
  .pdp-buynow-plus-mobile,
  a.pdp-buynow-btn-mobile,
  button.pdp-buynow-btn-mobile {
    font-size: 16px;
    line-height: 24px;
  }

  .pdp-buynow-plus-mobile,
  a.pdp-buynow-btn-mobile,
  button.pdp-buynow-btn-mobile {
    font-weight: 700;
    padding: 8px 24px;
    border-radius: 40px;
  }

  // an: Limit buttons width when having default_price
  .flex-shrink-0 .pdp-buynow-plus-mobile,
  .flex-shrink-0 .pdp-buynow-btn-mobile {
    max-width: 195px;
    white-space: inherit;
  }

  // an: Make buttons full width when not having default_price
  .flex-grow-1 .pdp-buynow-plus-mobile,
  .flex-grow-1 .pdp-buynow-btn-mobile {
    width: 100%;
  }

  .deal-unavailable-sticky {
    font-size: 16px;
    line-height: 21px;
  }

  div.pdp-buynow-price-sticky {
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
  }
  /* stylelint-enable selector-no-qualifying-type */

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s ease;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  /* potential temp styling below */
  .btn-primary {
    border: 1px solid #ffbc00;
    background-color: #ffbc00;
    color: #272727;
    padding: 12px 32px;
    font-size: 16px;
  }

  .btn-primary:hover,
  .btn-primary:disabled {
    border-color: #ffcb33;
    background-color: #ffcb33;
    color: #272727;
  }

  .btn-primary:active,
  .btn-primary:focus {
    border-color: #ffcb33 !important;
    background-color: #ffcb33 !important;
    color: #272727 !important;
  }

  .success {
    background: #0a840a;
    padding: 16px 20px;
    color: #fff;
    display: inline-flex;
    align-items: center;
  }
</style>
